import { customerAPiUrls } from 'constants/apiEndPoints';
import ApiUtil from '../utils/ApiUtil';
import QueryParamsBuilder from 'components/helpers/commonFunctions/QueryParamsBuilder';
import { type HoldingsFilters } from 'pages/employeesDetails/holdings/currentHoldings';
interface Customer {
  id: number;
  name: string;
  email: string;
  // other properties
}

const getAllCustomers = async ({
  page,
  size,
  sortBy,
  sortOrder,
  searchValue,
}: {
  page: number;
  size: number;
  sortBy?: string;
  sortOrder?: string;
  searchValue?: string;
}): Promise<Customer[]> => {
  const apiUrl = QueryParamsBuilder({
    baseUrl: customerAPiUrls.getAllCustomers,
    page,
    size,
    sortBy,
    sortOrder,
    searchValue,
  });
  return (await ApiUtil.getWithToken(apiUrl)) as Customer[];
};

const getCustomerById = async (customerId: number): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.getWithToken(`customer/${customerId}`)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });

const getCustomerOnBoardRequestById = async (customerId: number): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.getWithToken(
      `customer/${customerId}/${customerAPiUrls.customerOnBoardRequest}`,
    )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });

const rejectOnBoardingRequest = async (customerId: number, note: string): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.postWithToken(
      `customer/${customerId}/${customerAPiUrls.customerRejectOnboardRequest}`,
      { note, type: 'ONBOARD' },
    )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });

const approveOnBoardingRequest = async (customerId: number, email: string): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.postWithToken(
      `customer/${customerId}/${customerAPiUrls.customerAcceptOnboardRequest}`,
      { email },
    )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });

const rejectAvcRequest = async (customerId: number, note: string): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.postWithToken(
      `customer/${customerId}/${customerAPiUrls.customerRejectAvcRequest}`,
      { note, type: 'FundingLimitCheck' },
    )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });

const approveAvcRequest = async (customerId: number, email: string): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.postWithToken(
      `customer/${customerId}/${customerAPiUrls.customerAcceptAvcRequest}`,
      { email },
    )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });

const updateAvcDetails = async (customerId: number | string, isAvcPensionUser: boolean): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.putWithToken(`customer/${customerId}/avc-change`, { isAvcPensionUser })
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });

const updateFullPartialDeclaration = async (
  customerId: number | string,
  fullPartialDeclaration: 'Yes' | 'No',
): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.putWithToken(
      `customer/${customerId}/update-full-partial-replacement`,
      { fullPartialDeclaration },
    )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });

const assignApprover = async (customerId: number, approverId: number): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.putWithToken(`customer/${customerId}/approver/${approverId}`, {})
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });

const registerOnfidoVerification = async (userId: number): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.postWithToken(`customer/${userId}/register-onfido-verification`, {})
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });

const assignAvcApprover = async (customerId: number, approverId: number): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.putWithToken(`customer/${customerId}/avc-approver/${approverId}`, {})
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });

const getEmployeesById = async (employeesId: number): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.getWithToken(`employees/${employeesId}`)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });

const getCustomerRetirementDetailsbyId = async (id: string): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.getWithToken(`${String(customerAPiUrls.customerRetire)}${id}`)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });

const getCustomerMonthlyDepositbyId = async (id: string): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.getWithToken(`${String(customerAPiUrls.customerPayment)}${id}`)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });

// ExportExcel
const fetchExportExcelData = async (): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.getExcelFile('export-customer/')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((res: any) => {
        const blob = new Blob([res.data], {
          type: res.headers['content-type'],
        });
        const url = URL.createObjectURL(blob);
        resolve(url);
      })
      .catch((error) => {
        reject(error);
      });
  });
const fetchExportHoldingData = async (id: string): Promise<{ url: string; filename: string }> =>
  await new Promise((resolve, reject) => {
    ApiUtil.getExcelFile(`export-holding/${id}`)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((res: any) => {
        const blob = new Blob([res.data], {
          type: res.headers['content-type'],
        });
        const url = URL.createObjectURL(blob);

        // Extract filename from Content-Disposition
        const disposition = res.headers['content-disposition'];
        const filenameFromHeader = disposition?.match(/filename\*?=UTF-8''(.+\.xlsx)/)?.[1];
        const filename = filenameFromHeader ? decodeURIComponent(filenameFromHeader) : 'holdings.xlsx';

        resolve({ url, filename });
      })
      .catch((error) => {
        reject(error);
      });
  });

const exportMonthWiseHoldings = async (id: number, filters: HoldingsFilters): Promise<unknown> => {
  return await new Promise((resolve, reject) => {
    let apiUrl = QueryParamsBuilder({ baseUrl: `${customerAPiUrls.exportMonthWiseHoldings}/${id}` });

    const filterParams = new URLSearchParams();

    if (filters) {
      if (filters.startDate) {
        filterParams.append('startDate', filters.startDate);
      }

      if (filters.endDate) {
        filterParams.append('endDate', filters.endDate);
      }
    }

    if (filterParams.toString()) {
      apiUrl += `&${filterParams.toString()}`;
    }

    ApiUtil.getExcelFile(apiUrl)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((res: any) => {
        const blob = new Blob([res.data], {
          type: res.headers['content-type'],
        });
        const url = URL.createObjectURL(blob);
        resolve(url);
      })
      .catch((error) => {
        reject(error);
      });
  })
}

const getAllCustomerEmails = async (): Promise<Customer[]> => {
  const response = await ApiUtil.getWithToken(customerAPiUrls.getAllEmails);
  return response as Customer[];
};

const searchCustomer = async (searchValue: string): Promise<unknown> => {
  try {
    return await ApiUtil.postWithToken(`customer-search/`, {
      searchValue,
    });
  } catch (error) {
    throw new Error(`Unable to search employee. ${String(error)}`);
  }
};

const getCustomersListByName = async (value: string): Promise<unknown> => {
  try {
    return await ApiUtil.getWithToken(`customer-list/?name=${value}`);
  } catch (error) {
    throw new Error(`Unable to search customer. ${String(error)}`);
  }
};

const getCustomersTotalPot = async (userId: number): Promise<unknown> => {
  try {
    return await ApiUtil.getWithToken(
      `${customerAPiUrls.customerTotalPot}/${userId}`,
    );
  } catch (error) {
    throw new Error(`Unable to find customer. ${String(error)}`);
  }
};

const updateCustomerService = async (
  id: string,
  editedEmail: string,
  editedPhone: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updatedData?: any
): Promise<unknown> => {
  try {
    const [callingCode, phoneNumber] = splitPhoneNumber(editedPhone);
    return await ApiUtil.postWithToken(`update-customer-details/${id}`, {
      editedEmail,
      callingCode,
      phoneNumber,
      updatedData
    });
  } catch (error) {
    throw new Error(`Unable to update customer details. ${String(error)}`);
  }
};

const splitPhoneNumber = (phoneNumber: string): [string, string] => {
  const spaceIndex = phoneNumber.indexOf(' ');
  if (spaceIndex !== -1) {
    const callingCode = phoneNumber.substring(0, spaceIndex);
    const phone = phoneNumber.substring(spaceIndex + 1);
    return [callingCode, phone];
  } else {
    return ['', phoneNumber];
  }
};

const getCustomerDocs = async (id: string): Promise<unknown> => {
  try {
    return await ApiUtil.getWithToken(`customer/${id}/doc`);
  } catch (error) {
    throw new Error(`Unable to search customer. ${String(error)}`);
  }
};

const updateCustomerActiveStatusService = async (
  userId: string,
  activeStatus: string,
): Promise<unknown> => {
  try {

    return await ApiUtil.putWithToken(`customer/${userId}/active-status`, {
      activeStatus
    });
  } catch (error) {
    throw new Error(`Unable to update customer active status. ${String(error)}`);
  }
};
export {
  updateCustomerActiveStatusService,
  getCustomerDocs,
  getAllCustomers,
  getCustomerById,
  getEmployeesById,
  getCustomerRetirementDetailsbyId,
  getCustomerMonthlyDepositbyId,
  fetchExportExcelData,
  getAllCustomerEmails,
  searchCustomer,
  getCustomersListByName,
  getCustomersTotalPot,
  updateCustomerService,
  fetchExportHoldingData,
  getCustomerOnBoardRequestById,
  rejectOnBoardingRequest,
  approveOnBoardingRequest,
  rejectAvcRequest,
  approveAvcRequest,
  assignApprover,
  registerOnfidoVerification,
  assignAvcApprover,
  exportMonthWiseHoldings,
  updateAvcDetails,
  updateFullPartialDeclaration,
};
